import { useEffect, useLayoutEffect, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import AdminGate from "./admin/AdminGate";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import DocumentsPage from "./pages/DocumentsPage";
import CategoryGallery from "./pages/CategoryGallery";
import GlobalFloating from "./components/GlobalFloating";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import "./App.css";
import PopupFromConfig from "./components/PopupFromConfig";
import GalleryGroupPage from "./components/GalleryGroupPage";
import ReviewsPage from "./pages/ReviewsPage";
import { DEFAULT_PDF_PAGE_SLUG } from "./utils/pdfRouting";


export default function App() {
  const location = useLocation();
  const previousRouteRef = useRef(null);

  // Prevent browser auto-restoring old scroll positions on route changes.
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  // Scroll to top on page navigation (ignore hash-only updates on same page).
  useLayoutEffect(() => {
    const previousRoute = previousRouteRef.current;
    const isHashOnlyChange =
      previousRoute &&
      previousRoute.pathname === location.pathname &&
      previousRoute.search === location.search;

    previousRouteRef.current = {
      pathname: location.pathname,
      search: location.search,
    };

    if (isHashOnlyChange) return undefined;

    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      const targets = [
        document.scrollingElement,
        document.documentElement,
        document.body,
        document.getElementById("root"),
        document.querySelector(".app-root"),
        document.querySelector(".app-main"),
        document.querySelector(".App"),
        document.querySelector(".App-main"),
      ].filter(Boolean);

      targets.forEach((el) => {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      });
    };

    resetScroll();
    const raf1 = window.requestAnimationFrame(resetScroll);
    const raf2 = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(resetScroll)
    );
    const timers = [0, 80, 180, 320].map((delay) =>
      window.setTimeout(resetScroll, delay)
    );

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [location.pathname, location.search, location.key]);

  // Hide NavBar & Footer on admin routes
  const hideNavAndFooter = location.pathname.startsWith("/admin");

  return (
    <div className="app-root">
    

      {/* Main content area */}
      <main className={`app-main ${hideNavAndFooter ? "no-fixed-nav" : "has-fixed-nav"}`}>
          {!hideNavAndFooter && <NavBar />}
        <PopupFromConfig />

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path={`/${DEFAULT_PDF_PAGE_SLUG}`}
            element={<DocumentsPage pageSlug={DEFAULT_PDF_PAGE_SLUG} />}
          />
          <Route path="/admin" element={<AdminGate />} />
          <Route path="/gallery/:groupSlug" element={<GalleryGroupPage />} />
          <Route
            path="/gallery/:category/:subcategory"
            element={<CategoryGallery />}
          />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/:pdfPageSlug" element={<DocumentsPage />} />
        </Routes>
      </main>

      {!hideNavAndFooter && <Footer />}

      <GlobalFloating />
    </div>
  );
}
